import { MainLayout } from '@/layouts/MainLayout';
import { AccessDeniedPage } from '@/shared/components/AccessDeniedPage';
import { USER_PERMISSIONS } from '@/shared/types/permissions';
import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { BrokenSubscriptionEditRoute } from './components/BrokenSubscriptionEditRoute';
import { BrokenSubscriptionRedirectToView } from './components/BrokenSubscriptionRedirectToView';
import { DefaultRedirect } from './components/DefaultRedirect';
import { PageLoader } from './components/PageLoader';
import { PermissionRoute } from './components/PermissionRoute';
import { ScrollToTop } from './components/ScrollToTop';
import {
  AuditLogsPage,
  BannerForm,
  BannersList,
  BrokenSubscriptionDetailPage,
  BrokenSubscriptionsPage,
  CustomerOverview,
  CustomersList,
  DeepLinksManagementPage,
  DiscountForm,
  DiscountsList,
  DsAgentForm,
  DsAgentsList,
  PaymentRetriesList,
  ProductForm,
  ProductOrderingPage,
  ProductsList,
  RafSettingsPage,
  RemovalBatchDetails,
  RemovalBatchForm,
  RemovalBatchesList,
  ReplacementBatchDetails,
  ReplacementBatchForm,
  ReplacementBatchesList,
  TrialFunnelLogsPage,
  TrialWebhookReprocessPage,
  UpsellConfigPage,
  UserForm,
  UsersList,
  RolesList,
  RoleForm,
} from './lazyPages';

export const AppRoutes: React.FC = () => {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<MainLayout />}>
          <Route path="access-denied" element={<AccessDeniedPage />} />
          <Route
            path="banners"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.BannersView]}>
                <Suspense fallback={<PageLoader />}>
                  <BannersList />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="banners/new"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.BannersEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <BannerForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="banners/:id"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.BannersEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <BannerForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="discounts"
            element={
              <PermissionRoute
                permissions={[
                  USER_PERMISSIONS.SubscriptionDiscountsView,
                  USER_PERMISSIONS.SubscriptionDiscountsEdit,
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <DiscountsList />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="discounts/new"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.SubscriptionDiscountsEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <DiscountForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="discounts/:id"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.SubscriptionDiscountsEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <DiscountForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="deep-links-management"
            element={
              <Suspense fallback={<PageLoader />}>
                <DeepLinksManagementPage />
              </Suspense>
            }
          />
          <Route
            path="users/roles"
            element={
              <PermissionRoute
                permissions={[USER_PERMISSIONS.UserRolesView, USER_PERMISSIONS.UserRolesEdit]}
              >
                <Suspense fallback={<PageLoader />}>
                  <RolesList />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="users/roles/new"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.UserRolesEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <RoleForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="users/roles/:id"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.UserRolesEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <RoleForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="users"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.UsersView]}>
                <Suspense fallback={<PageLoader />}>
                  <UsersList />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="users/new"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.UsersEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <UserForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="users/:id"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.UsersEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <UserForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="ds-agents"
            element={
              <PermissionRoute
                permissions={[USER_PERMISSIONS.DSAgentsView, USER_PERMISSIONS.DSAgentsEdit]}
              >
                <Suspense fallback={<PageLoader />}>
                  <DsAgentsList />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="ds-agents/new"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.DSAgentsEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <DsAgentForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="ds-agents/:id"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.DSAgentsEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <DsAgentForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="customers"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.CustomersView]}>
                <Suspense fallback={<PageLoader />}>
                  <CustomersList />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="customers/:id"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.CustomersView]}>
                <Suspense fallback={<PageLoader />}>
                  <CustomerOverview />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="payment-retries"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.CustomersPaymentRetriesView]}>
                <Suspense fallback={<PageLoader />}>
                  <PaymentRetriesList />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="audit-logs"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.AuditLogView]}>
                <Suspense fallback={<PageLoader />}>
                  <AuditLogsPage />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="replacement-batches"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.ReplacementBatchesView]}>
                <Suspense fallback={<PageLoader />}>
                  <ReplacementBatchesList />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="replacement-batches/new"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.ReplacementBatchesEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <ReplacementBatchForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="replacement-batches/:id/details"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.ReplacementBatchesView]}>
                <Suspense fallback={<PageLoader />}>
                  <ReplacementBatchDetails />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="replacement-batches/:id/edit"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.ReplacementBatchesEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <ReplacementBatchForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="removal-batches"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.RemovalBatchesView]}>
                <Suspense fallback={<PageLoader />}>
                  <RemovalBatchesList />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="removal-batches/new"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.RemovalBatchesEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <RemovalBatchForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="removal-batches/:id/details"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.RemovalBatchesView]}>
                <Suspense fallback={<PageLoader />}>
                  <RemovalBatchDetails />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="removal-batches/:id/edit"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.RemovalBatchesEdit]}>
                <Suspense fallback={<PageLoader />}>
                  <RemovalBatchForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="trial-funnel-logs"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.TrialFunnelLogsView]}>
                <Suspense fallback={<PageLoader />}>
                  <TrialFunnelLogsPage />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route path="prevented-mtos" element={<Navigate to="/trial-funnel-logs" replace />} />
          <Route path="blocked-checkouts" element={<Navigate to="/trial-funnel-logs" replace />} />
          <Route
            path="trial-funnel-errors"
            element={<Navigate to="/trial-funnel-logs" replace />}
          />
          <Route
            path="products"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.ProductsView]}>
                <Suspense fallback={<PageLoader />}>
                  <ProductsList />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="product-ordering"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.ProductsView]}>
                <Suspense fallback={<PageLoader />}>
                  <ProductOrderingPage />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="products/new"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.ProductsCreate]}>
                <Suspense fallback={<PageLoader />}>
                  <ProductForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="products/:id"
            element={
              <PermissionRoute
                permissions={[
                  USER_PERMISSIONS.ProductsView,
                  USER_PERMISSIONS.ProductsCreate,
                  USER_PERMISSIONS.ProductsImagesEdit,
                  USER_PERMISSIONS.ProductsSubscriptionConfigurationEdit,
                  USER_PERMISSIONS.ProductsPromotionsBadgesEdit,
                  USER_PERMISSIONS.ProductsVarietyPackContentsEdit,
                  USER_PERMISSIONS.ProductsAdditionalInformationEdit,
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <ProductForm />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="broken-subscriptions/:shopifyCustomerId/view"
            element={
              <PermissionRoute
                permissions={[
                  USER_PERMISSIONS.BrokenSubscriptionsView,
                  USER_PERMISSIONS.BrokenSubscriptionsEdit,
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <BrokenSubscriptionDetailPage mode="view" />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="broken-subscriptions/:shopifyCustomerId/edit"
            element={
              <PermissionRoute
                permissions={[
                  USER_PERMISSIONS.BrokenSubscriptionsView,
                  USER_PERMISSIONS.BrokenSubscriptionsEdit,
                ]}
              >
                <BrokenSubscriptionEditRoute />
              </PermissionRoute>
            }
          />
          <Route
            path="broken-subscriptions/:shopifyCustomerId"
            element={
              <PermissionRoute
                permissions={[
                  USER_PERMISSIONS.BrokenSubscriptionsView,
                  USER_PERMISSIONS.BrokenSubscriptionsEdit,
                ]}
              >
                <BrokenSubscriptionRedirectToView />
              </PermissionRoute>
            }
          />
          <Route
            path="broken-subscriptions"
            element={
              <PermissionRoute
                permissions={[
                  USER_PERMISSIONS.BrokenSubscriptionsView,
                  USER_PERMISSIONS.BrokenSubscriptionsEdit,
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <BrokenSubscriptionsPage />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="upsell-config"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.UpsellsConfigurationView]}>
                <Suspense fallback={<PageLoader />}>
                  <UpsellConfigPage />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="raf-settings"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.RafConfigurationView]}>
                <Suspense fallback={<PageLoader />}>
                  <RafSettingsPage />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="system-admin-actions"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.SystemAdmin]}>
                <Suspense fallback={<PageLoader />}>
                  <TrialWebhookReprocessPage />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route
            path="upsell-config"
            element={
              <PermissionRoute permissions={[USER_PERMISSIONS.UpsellsConfigurationView]}>
                <Suspense fallback={<PageLoader />}>
                  <UpsellConfigPage />
                </Suspense>
              </PermissionRoute>
            }
          />
          <Route index element={<DefaultRedirect />} />
          <Route path="*" element={<DefaultRedirect />} />
        </Route>
      </Routes>
    </>
  );
};
